import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { fetchMe, loginWithPassword, logoutApi, formatApiErrorDetail } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = signed in
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      return me;
    } catch (err) {
      // 401 on initial /me when anonymous is expected — only log unexpected errors.
      if (err?.response?.status && err.response.status !== 401) {
        console.error('Auth refresh error:', err);
      }
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we are returning from Google OAuth, AuthCallback handles it.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const u = await loginWithPassword({ email, password });
      setUser(u);
      setLoading(false);
      return { ok: true, user: u };
    } catch (err) {
      return {
        ok: false,
        error: formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, setUser }),
    [user, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
